import {
  Button,
  ButtonProps,
  Editable,
  EditableInput,
  EditablePreview,
  Flex,
  Input,
  useEditableControls,
} from '@chakra-ui/react';
import { ReactComponent as FolderIcon } from 'assets/icons/folder.svg';
import { MouseEvent, ReactElement, useEffect, useRef, useState } from 'react';

import { FOLDERS_NAME_MAX_CHARS_ALLOWED, FolderProps, useOnImageDrop } from './Folders.helper';

type EditableControlsProps = {
  folder: FolderProps;
  isActive?: boolean;
  onClick?: (folder: FolderProps) => void;
  preview: ReactElement;
  edit: ReactElement;
  editOnCreate?: boolean;
  isOnDrag?: boolean;
  buttonProps?: ButtonProps;
};
const EditableControls = ({
  preview,
  edit,
  isActive,
  folder,
  onClick,
  editOnCreate,
  isOnDrag,
  buttonProps,
}: EditableControlsProps) => {
  const { isEditing, getEditButtonProps } = useEditableControls();
  const editButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Enable edit from the editableControls
   * @param e MouseEvent
   */
  const enableEdit = (e: MouseEvent<HTMLButtonElement>) => {
    const { onClick } = getEditButtonProps();
    onClick?.(e);
  };

  /**
   * Helper to pass folder on click
   * @param e MouseEvent
   */
  const onSelectFolder = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(folder);
  };

  /**
   * edit the field after creation if the flag is set
   */
  useEffect(() => {
    if (editOnCreate) {
      editButtonRef?.current?.click();
    }
  }, [editOnCreate]);

  return (
    <Flex justifyContent='center'>
      <Button
        leftIcon={folder.icon || <FolderIcon />}
        variant='ghost-with-active'
        size='sm'
        onClick={editOnCreate ? enableEdit : onSelectFolder}
        justifyContent='start'
        minW='100%'
        isActive={isActive}
        mb={2}
        onDoubleClick={enableEdit}
        ref={editButtonRef}
        background={isOnDrag ? 'blackAlpha.200' : 'none'}
        data-testid={`folder-menu-${folder.value}`}
        fontWeight='medium'
        _active={{
          bg: 'hover.blue',
          '& path': {
            stroke: 'upgrade.blue.700',
          },
          color: 'upgrade.blue.700',
          ...buttonProps?._active,
        }}
        {...buttonProps}
      >
        {isEditing ? edit : preview}
      </Button>
    </Flex>
  );
};

type FolderItemProps = {
  folder: FolderProps;
  selectedFolder?: FolderProps;
  isActive?: boolean;
  isEditing?: boolean;
  disableEditing?: boolean;
  onClick?: (folder: FolderProps) => void;
  onEdit?: (name: string) => void;
  selectedImages: string[];
  isButtonList?: boolean;
  buttonProps?: ButtonProps;
  selectImages: (imageId: string[]) => void;
};

export const FolderItem = ({
  folder,
  onClick,
  isActive,
  isEditing,
  onEdit,
  disableEditing = false,
  selectedImages,
  selectedFolder,
  isButtonList = false,
  buttonProps,
  selectImages,
}: FolderItemProps) => {
  const [isOnDrag, setIsOnDrag] = useState(false);
  const { onImageDrop } = useOnImageDrop();

  /**
   * Handler to manage when a user drop the image on a folder
   */
  const handleWidgetDrop = async () => {
    setIsOnDrag(false);
    selectImages([]);
    const result = await onImageDrop({ moveToFolder: folder, selectedImages, selectedFolder });
    // refresh the selected folder
    if (result?.selectedFolder) onClick?.(result.selectedFolder);
  };

  /**
   * Event handler to change isDragging status
   * @param e
   */
  const handleDragOver = (e: React.DragEvent<HTMLDivElement | HTMLButtonElement>) => {
    e.preventDefault();
    setIsOnDrag(true);
  };

  /**
   * Event handler to change isDragging status
   * @param e
   */
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement | HTMLButtonElement>) => {
    e.preventDefault();
    setIsOnDrag(false);
  };

  if (isButtonList) {
    const onSelectFolder = () => {
      onClick?.(folder);
    };
    return (
      <Button
        onDrop={handleWidgetDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        leftIcon={<FolderIcon fill='none' />}
        variant='outline'
        size='sm'
        onClick={onSelectFolder}
        isActive={isActive}
        data-testid='folder-button'
        background={isOnDrag ? 'blackAlpha.200' : 'none'}
        fontWeight='medium'
      >
        {folder.label}
      </Button>
    );
  }

  return (
    <Editable
      defaultValue={folder.label}
      isPreviewFocusable={false}
      onSubmit={onEdit}
      isDisabled={disableEditing}
      onDrop={handleWidgetDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
    >
      <EditableControls
        onClick={onClick}
        isActive={isActive}
        folder={folder}
        editOnCreate={isEditing}
        isOnDrag={isOnDrag}
        buttonProps={buttonProps}
        preview={
          <EditablePreview
            style={{
              cursor: 'pointer',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          />
        }
        edit={
          <Input
            maxLength={FOLDERS_NAME_MAX_CHARS_ALLOWED}
            textAlign='left'
            backgroundColor='transparent'
            size='xs'
            as={EditableInput}
          />
        }
      />
    </Editable>
  );
};

FolderItem.displayName = 'FolderItem';
